"use client";

import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "~/lib/utils";
import { useUser } from "~/modules/user/hooks/useUser";
import { useToaster } from "~/modules/toaster/hooks/useToaster";
import { api } from "~/utils/api";
import { useAssetChat } from "../../hooks/useAssetChat";
import { DockLauncher } from "./DockLauncher";
import { DockAssetChat } from "./DockAssetChat";
import { AssetPicker } from "./AssetPicker";
import { ConversationList } from "./ConversationList";
import { IconButton, NewChatButton, PaneHeader } from "./dock-frame";
import { FeedbackButton } from "./FeedbackButton";
import { assetLabel } from "./shared";
import { usePathname } from "next/navigation";
import { Routes } from "~/routes";

// // // //

/**
 * The dock's internal view. `list` shows the user's conversations; `picker` lets
 * them choose an asset for a new chat; `chat` renders {@link DockAssetChat} for a
 * single asset.
 */
type DockView =
    | { kind: "list" }
    | { kind: "picker" }
    | {
          kind: "chat";
          assetCorpusId: string;
          assetName: string;
          // New chats (picker / Redux) start empty; opening a list row resumes
          // that asset's latest thread.
          startFresh: boolean;
      };

// Open as the compact popover by default (the design's primary surface); the
// user can enlarge it in place via the maximize control.
const OPEN_MAXIMIZED_BY_DEFAULT = false;

// Allowed pathnames to show the chat dock
const ALLOWED_PATHNAMES = [Routes.home, Routes.assetScreener];

/**
 * GlobalChatDock
 *
 * A fixed, bottom-right AI chat dock mounted globally in `_app.tsx`, matching
 * the "In-app AI chat — Grounded" design. The launcher opens a compact popover
 * that:
 *   - lists the user's asset-chat conversations (bucketed Today / Earlier),
 *   - starts a new chat (pick an asset, or via the `openNewChat` Redux action),
 *   - opens a conversation into the reused {@link DockAssetChat} surface.
 *
 * Maximizing keeps the same single-column panel anchored in its bottom-right
 * position and simply grows it much larger (no modal, no backdrop). The chat
 * pane is keyed, so an in-flight conversation is never remounted when toggling
 * maximize.
 *
 * It is the sole consumer of the asset_chat Redux store: any component can call
 * `useAssetChat().openNewChat({ assetCorpusId, assetName })` to open a fresh
 * chat here. Only rendered for logged-in users.
 */
export function GlobalChatDock() {
    const { isLoggedIn } = useUser();
    // Gated behind the same flag as the inline AssetChat (see AssetResultRowDetails).
    const pathname = usePathname();
    const { addToaster } = useToaster();
    const { openNewChatRequest, clearOpenNewChatRequest, setChatOpen } =
        useAssetChat();
    const utils = api.useUtils();

    const [isOpen, setIsOpen] = useState(false);
    const [view, setView] = useState<DockView>({ kind: "list" });
    const [isMaximized, setIsMaximized] = useState(OPEN_MAXIMIZED_BY_DEFAULT);
    // Bumped whenever a chat is opened, so DockAssetChat remounts and re-hydrates
    // — important when re-opening the same asset the dock is already showing.
    // Toggling maximize does NOT bump it, so the live chat is preserved.
    const [chatNonce, setChatNonce] = useState(0);

    const conversationsQuery = api.assetChat.listConversations.useQuery(
        undefined,
        { enabled: isLoggedIn && isOpen },
    );

    const deleteConversation = api.assetChat.delete.useMutation({
        onSuccess: async () => {
            await utils.assetChat.listConversations.invalidate();
        },
        onError: (error) => {
            addToaster({
                variant: "danger",
                message: error.message || "Couldn't delete that chat.",
            });
        },
    });

    // Open the chat window for an asset. No conversation is written to the DB
    // here — the first submitted message persists it via the /api/asset-chat
    // route. `startFresh` opens an empty chat; otherwise DockAssetChat hydrates
    // the asset's latest thread.
    const openChat = useCallback(
        (assetCorpusId: string, assetName: string, startFresh: boolean) => {
            setChatNonce((nonce) => nonce + 1);
            setView({ kind: "chat", assetCorpusId, assetName, startFresh });
        },
        [],
    );

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setIsMaximized(OPEN_MAXIMIZED_BY_DEFAULT);
        setView({ kind: "list" });
    }, []);

    const handleBack = useCallback(() => {
        setView({ kind: "list" });
        // A first message may have just persisted this chat — refresh so it
        // shows up in the list.
        void utils.assetChat.listConversations.invalidate();
    }, [utils]);

    // Consume Redux `openNewChat` requests dispatched from elsewhere in the app.
    useEffect(() => {
        if (!openNewChatRequest) {
            return;
        }
        if (!isLoggedIn) {
            clearOpenNewChatRequest();
            return;
        }
        setIsOpen(true);
        openChat(
            openNewChatRequest.assetCorpusId,
            openNewChatRequest.assetName,
            true,
        );
        clearOpenNewChatRequest();
        // Keyed on requestId so repeat requests for the same asset still fire.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openNewChatRequest?.requestId]);

    // Mirror whether a chat conversation is currently open into Redux so other
    // surfaces (e.g. the screener's data-room badge) can avoid reopening it.
    const isChatViewOpen = isOpen && view.kind === "chat";
    useEffect(() => {
        setChatOpen(isChatViewOpen);
    }, [isChatViewOpen, setChatOpen]);

    // Escape minimizes the enlarged window, then closes the popover.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            // defaultPrevented: an inner surface (e.g. the feedback popover)
            // already consumed this Escape press.
            if (event.key !== "Escape" || event.defaultPrevented) {
                return;
            }
            if (isMaximized) {
                setIsMaximized(false);
            } else {
                handleClose();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isOpen, isMaximized, handleClose]);

    // If the user is not logged in or the chat is not enabled, or the pathname is not allowed, return null
    if (!isLoggedIn || !ALLOWED_PATHNAMES.includes(pathname as Routes)) {
        return null;
    }

    const conversations = conversationsQuery.data ?? [];
    const showBack = view.kind === "picker" || view.kind === "chat";
    const paneTitle =
        view.kind === "picker"
            ? "New chat"
            : view.kind === "chat"
              ? view.assetCorpusId
              : "Chats";
    // The compact popover fills a definite height (so inner regions scroll)
    // except for the empty conversation list, which hugs its content.
    const compactFillsHeight =
        view.kind === "picker" ||
        view.kind === "chat" ||
        (view.kind === "list" && conversations.length > 0);

    const list = () => (
        <ConversationList
            dense={false}
            isLoading={conversationsQuery.isLoading}
            isError={conversationsQuery.error != null}
            conversations={conversations}
            deletingId={
                deleteConversation.isLoading
                    ? deleteConversation.variables?.id
                    : undefined
            }
            onOpen={(assetID) => openChat(assetID, assetLabel(assetID), false)}
            onDelete={(id) => deleteConversation.mutate({ id })}
            onNewChat={() => setView({ kind: "picker" })}
            onRetry={() => conversationsQuery.refetch()}
        />
    );

    return (
        <>
            {/* The single persistent panel — compact popover or enlarged in place.
                Both states are anchored to the same bottom-right corner; maximizing
                just grows the window (never a centered modal). */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        role="dialog"
                        aria-label="AI chat"
                        // Opens instantly (no enter animation); closes with a
                        // 250ms decelerating fade before unmounting.
                        initial={false}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: 0.25,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className={cn(
                            "fixed right-[22px] bottom-[90px] z-[110] flex origin-bottom flex-col overflow-clip rounded-t-xl rounded-b-2xl border-[0.5px] bg-card",
                            "shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12),0_3px_6px_-2px_rgba(0,0,0,0.05),0_1px_1px_0_rgba(0,0,0,0.04)]",
                            isMaximized
                                ? "h-[min(48rem,calc(100vh-7.5rem))] w-[min(40rem,calc(100vw-2rem))]"
                                : cn(
                                      "w-[400px] max-w-[calc(100vw-2rem)]",
                                      // A definite height lets inner regions scroll;
                                      // max-height keeps short states compact.
                                      compactFillsHeight
                                          ? "h-[min(40rem,calc(100vh-9rem))]"
                                          : "max-h-[min(40rem,calc(100vh-9rem))]",
                                  ),
                        )}
                    >
                        <PaneHeader
                            heightClass={isMaximized ? "min-h-14" : "min-h-13"}
                            eyebrow={
                                view.kind === "chat"
                                    ? "RWA.xyz AI Diligence"
                                    : undefined
                            }
                            title={paneTitle}
                            onBack={showBack ? handleBack : undefined}
                        >
                            {view.kind === "list" &&
                                conversations.length > 0 && (
                                    <NewChatButton
                                        onClick={() =>
                                            setView({ kind: "picker" })
                                        }
                                    />
                                )}
                            <FeedbackButton
                                view={view.kind}
                                assetCorpusId={
                                    view.kind === "chat"
                                        ? view.assetCorpusId
                                        : undefined
                                }
                            />
                            <IconButton
                                label={isMaximized ? "Minimize" : "Maximize"}
                                onClick={() => setIsMaximized((max) => !max)}
                            >
                                {isMaximized ? <Minimize2 /> : <Maximize2 />}
                            </IconButton>
                            <IconButton
                                label="Close chats"
                                onClick={handleClose}
                            >
                                <X />
                            </IconButton>
                        </PaneHeader>

                        {view.kind === "list" && (
                            <div className="min-h-0 flex-1">{list()}</div>
                        )}
                        {view.kind === "picker" && (
                            <div className="min-h-0 flex-1">
                                <AssetPicker
                                    onPick={(asset) =>
                                        openChat(asset.id, asset.name, true)
                                    }
                                />
                            </div>
                        )}
                        {view.kind === "chat" && (
                            <DockAssetChat
                                key={chatNonce}
                                corpusAssetId={view.assetCorpusId}
                                assetName={view.assetName}
                                startFresh={view.startFresh}
                                className="min-h-0 flex-1"
                            />
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/*
             * Launcher — always visible; the dock is no longer a modal.
             *
             * `right-20` (5rem) is the resting offset from the viewport edge.
             * Radix Select/Dialog (via react-remove-scroll-bar) locks body scroll
             * when open and removes the scrollbar, which would otherwise shift
             * fixed right-positioned UI left by the scrollbar width. That library
             * sets `--removed-body-scroll-bar-size` on `body` while locked (we do
             * not define it). Adding it to the calc keeps the launcher visually
             * stable; the `0px` fallback is a no-op when nothing is scroll-locked.
             */}
            <div className="fixed right-[calc(5rem+var(--removed-body-scroll-bar-size,0px))] bottom-4.5 z-[110]">
                <DockLauncher
                    isOpen={isOpen}
                    onClick={() => (isOpen ? handleClose() : setIsOpen(true))}
                />
            </div>
        </>
    );
}
